import { Zap } from "lucide-react";
import type { TestRun } from "@/services/testing";
import { summarizeRunHistory } from "@/utils/testing/runHistory";

/**
 * The one-line record of how this test has behaved across runs, shown at the foot of the thread.
 *
 * Renders nothing until there are at least two finished runs — before that the result cards above
 * already say everything, and a "1 run — 1 passed" line is pure noise.
 */
export function ChatSessionSummary({ runs }: { runs: TestRun[] }) {
	const summary = summarizeRunHistory(runs);
	if (!summary) return null;

	return (
		<div className="tst-measure pb-3">
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
				<span>
					{summary.total} runs — <span className="text-success-strong">{summary.passed} passed</span>
					{summary.failed > 0 && (
						<>
							, <span className="text-destructive-strong">{summary.failed} failed</span>
						</>
					)}
				</span>
				{summary.flaky && (
					<span
						className="inline-flex items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 font-medium text-warning-strong"
						title="This plan has flipped between pass and fail across runs without changing — likely a flaky test (timing/async or environment)."
					>
						<Zap className="h-3 w-3" /> Flaky
					</span>
				)}
				{summary.lastFailureCategory && (
					<span className="font-mono">last failure: {summary.lastFailureCategory}</span>
				)}
			</div>
		</div>
	);
}
